import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { storefrontApi } from '../lib/api'
import { productImageUrl } from '../lib/images'
import type { HomePayload } from '../types/commerce'
import { ProductRail } from '../components/ProductRail'

const fallbackCategories = [
  { id: 'roupas', name: 'Roupas', slug: 'roupas', image_path: null, description: 'Peças essenciais com acabamento elegante.' },
  { id: 'acessorios', name: 'Acessórios', slug: 'acessorios', image_path: null, description: 'Detalhes que completam a composição.' },
  { id: 'novidades', name: 'Novidades', slug: 'novidades', image_path: null, description: 'Uma seleção fresca para a temporada.' },
  { id: 'destaques', name: 'Destaques', slug: 'destaques', image_path: null, description: 'Os favoritos da curadoria GK Maison.' }
]

export function Home() {
  const [data, setData] = useState<HomePayload | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    storefrontApi.home().then(setData).catch(err => setError(err.message))
  }, [])

  if (error) return <div className="page-message">{error}</div>
  if (!data) return <div className="page-message">Carregando coleção...</div>

  const hero = data.banners.find(banner => banner.placement === 'hero')
  const promo = data.banners.find(banner => banner.placement === 'collection')
  const editorial = data.banners.find(banner => banner.placement === 'editorial')
  const categoryTiles = (data.categories.length ? data.categories : fallbackCategories).slice(0, 4)

  return (
    <div className="store-home">
      <section className={`atelier-hero ${hero?.image_path ? 'atelier-hero--media' : 'atelier-hero--fallback'}`}>
        {hero?.image_path && (
          <picture className="atelier-hero__media">
            {hero.mobile_image_path && <source media="(max-width: 699px)" srcSet={productImageUrl(hero.mobile_image_path)} />}
            <img src={productImageUrl(hero.image_path)} alt={hero.title} />
          </picture>
        )}
        <div className="atelier-hero__shade" />
        <div className="atelier-hero__content">
          <span>{hero?.eyebrow ?? 'GK MAISON · CURADORIA CONTEMPORÂNEA'}</span>
          <h1>{hero?.title ?? 'ELEGÂNCIA ESSENCIAL'}</h1>
          <p>{hero?.subtitle ?? 'Uma seleção de linhas limpas, tons naturais e peças feitas para permanecer.'}</p>
          <Link className="atelier-button atelier-button--light" to={hero?.link_url ?? '/produtos'}>
            {hero?.link_label ?? 'VER COLEÇÃO'}
          </Link>
        </div>
      </section>

      <section
        className={`home-promo ${promo?.image_path ? 'home-promo--media' : ''}`}
        style={promo?.image_path ? { backgroundImage: `url(${productImageUrl(promo.image_path)})` } : undefined}
      >
        <div className="home-promo__veil" />
        <div className="home-promo__content">
          <span>{promo?.eyebrow ?? 'SELEÇÃO DA CASA'}</span>
          <h2>{promo?.title ?? 'DESTAQUES DA SEMANA'}</h2>
          <p>{promo?.subtitle ?? 'Descubra peças escolhidas para uma composição simples, sofisticada e versátil.'}</p>
          <Link className="atelier-button atelier-button--ink" to={promo?.link_url ?? '/produtos?sort=featured'}>
            {promo?.link_label ?? 'DESCOBRIR'}
          </Link>
        </div>
      </section>

      <section className="home-category-section section-shell">
        <div className="home-category-heading">
          <span>EXPLORE</span>
          <h2>Encontre seu próximo essencial</h2>
        </div>

        <div className="home-category-grid">
          {categoryTiles.map((category, index) => (
            <Link
              key={category.id}
              className="home-category-card"
              to={category.id === 'novidades'
                ? '/produtos?sort=newest'
                : category.id === 'destaques'
                  ? '/produtos?sort=featured'
                  : `/produtos?category=${category.slug}`}
            >
              <div className="home-category-card__title">
                <h3>{category.name}</h3>
                <span>0{index + 1}</span>
              </div>
              <div className={`home-category-card__media home-category-card__media--${index + 1}`}>
                {category.image_path
                  ? <img src={productImageUrl(category.image_path)} alt="" />
                  : <div className="home-category-card__placeholder" aria-hidden="true"><span>GK</span></div>}
              </div>
              <div className="home-category-card__copy">
                <p>{category.description ?? 'Descubra a seleção GK Maison.'}</p>
                <span>VER MAIS</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <ProductRail title="LANÇAMENTOS" items={data.new_arrivals} />

      {editorial && (
        <section className="atelier-editorial" style={{ backgroundImage: `url(${productImageUrl(editorial.image_path)})` }}>
          <div className="atelier-editorial__veil" />
          <div className="atelier-editorial__content">
            <span>{editorial.eyebrow ?? 'EDITORIAL'}</span>
            <h2>{editorial.title}</h2>
            {editorial.subtitle && <p>{editorial.subtitle}</p>}
            <Link className="atelier-button atelier-button--light" to={editorial.link_url ?? '/produtos'}>
              {editorial.link_label ?? 'DESCOBRIR'}
            </Link>
          </div>
        </section>
      )}

      <ProductRail title="DESTAQUES" items={data.featured} />

      {data.collections.length > 0 && (
        <section className="section-shell collection-list">
          <div className="section-heading"><h2>COLEÇÕES</h2></div>
          <div className="collection-grid">
            {data.collections.slice(0, 3).map(collection => (
              <Link key={collection.id} to={`/produtos?collection=${collection.slug}`} className="collection-card">
                <div className="collection-card__media">
                  {collection.image_path
                    ? <img src={productImageUrl(collection.image_path)} alt={collection.name} />
                    : <div className="image-placeholder">GK</div>}
                </div>
                <h3>{collection.name}</h3>
                <span>VER COLEÇÃO</span>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
